<template>
  <v-data-table
    :headers="thead"
    :items="items"
    :pagination.sync="pagination"
    :total-items="totalCount"
    :rows-per-page-items="rowsPerPageItems"
    :disable-initial-sort="true"
  >
    <template v-slot:headers="props">
      <tr>
        <th v-if="selectable">
          <v-checkbox v-model="selectAll" style="height: 24px;" />
        </th>

        <th
          v-for="header in props.headers"
          :key="header.text"
          class="column"
          style="text-align: left"
          :class="thClasses(header)"
          :width="header.width"
          @click="changeSort(header)"
        >
          {{ header.text }}
          <v-icon v-if="header.sortable !== false" small>arrow_upward</v-icon>
        </th>
      </tr>
    </template>

    <template v-slot:items="props">
      <td v-if="selectable">
        <v-checkbox v-model="selected" style="height: 24px;" :value="props.item[selectableProperty]" />
      </td>

      <td v-for="header in headers" :key="header.value" class="data-table-word-break">
        <a v-if="header.link" @click.prevent="$emit('details', props.item)">
          {{ renderValue(props.item, header) }}
        </a>

        <span
          v-else-if="renderValue(props.item, header) && renderValue(props.item, header).html"
          v-html="renderValue(props.item, header).html"
        />

        <span v-else>{{ renderValue(props.item, header) }}</span>
      </td>

      <actions :actions="actions" :object="props.item" :entity-type="entityType" v-on="$listeners" />
    </template>
  </v-data-table>
</template>

<script>
import { isOfficial } from '@witkac/kartamiejska-shared';
import { tryParseBoolean } from '@witkac/utils/tryParseBoolean';
import { tryParseInt } from '@witkac/utils/tryParseInt';

import Actions from './table/Actions';

/**
 * @vuese
 * @group Components/Material
 * Tabelka
 */
export default {
  components: { Actions },
  props: {
    // Tabela z obiektami
    // {
    //   text: string,
    //   value: string,
    //   link?: boolean,
    //   order?: string //deprecated,
    //   orderBy?: Array<String> || string,
    //   sortable?: boolean,
    // }
    headers: {
      type: Array,
      required: true
    },
    // Lista obiektów do wyświetlenia w tabeli
    // Wyświetlane będą pola z `value` nagłówków
    items: {
      type: Array,
      required: true
    },
    // Całkowita liczba elementów
    totalCount: {
      type: Number,
      required: false
    },
    // Czy wyświetlać kolumnę z akcjami
    actions: {
      type: Array,
      required: false
    },
    // Szerokość kolumny z akcjami
    actionsWidth: {
      type: String,
      required: false,
      default: '170px'
    },
    // Wyłączenie sortowania
    disableInitialSort: {
      type: Boolean,
      required: false,
      default: false
    },
    // Typ encji
    entityType: {
      type: String,
      required: false
    },
    // Czy można zaznaczać wiersze
    selectable: {
      type: Boolean,
      required: false,
      default: false
    },
    // Property zwracane po zaznaczeniu
    selectableProperty: {
      type: String,
      default: 'id',
      required: false
    }
  },
  data() {
    return {
      pagination: {
        page: tryParseInt(this.$route.query.page) || 1,
        rowsPerPage: tryParseInt(this.$route.query.count) || 10,
        sortBy: this.$route.query.order,
        descending: tryParseBoolean(this.$route.query.desc)
      },
      rowsPerPageItems: [10, 25, 50, { text: 'Wszystkie', value: -1 }],
      preventReload: true,
      selected: [],
      selectAll: false
    };
  },
  computed: {
    thead() {
      return this.actions
        ? [...this.headers, { value: 'id', text: ' ', sortable: false, width: this.actionsWidth }]
        : this.headers;
    },
    isOfficial
  },
  watch: {
    '$route.query.page'(page) {
      this.pagination.page = tryParseInt(page);
    },
    pagination: {
      deep: true,
      handler(newValue) {
        this.paginationStart(newValue);
      }
    },
    selected: {
      deep: true,
      immediate: true,
      handler(selected) {
        this.selectAll = this.items && this.items.length && selected.length === this.items.length;
        this.$emit('selected', selected);
      }
    },
    selectAll(val) {
      if (val) {
        this.selected = this.items.map(item => item.id);
      } else if (this.items.length === this.selected.length) {
        this.selected = [];
      }
    },
    items: {
      deep: true,
      handler() {
        this.selected = [];
      }
    }
  },
  methods: {
    /**
     * @vuese
     * Emituje event wywołujący paginacje
     */
    paginationStart(pagination) {
      setTimeout(() => {
        const query = {
          page: pagination.page,
          count: pagination.rowsPerPage,
          desc: pagination.descending,
          ...this.getPaginationValue(pagination.sortBy)
        };
        const anyValueChanged = Object.keys(query).some(field => {
          const queryField = query[field];
          const routeField = this.$route.query[field];
          const queryValue = Array.isArray(queryField) ? queryField.join(',') : (queryField || '').toString();
          const routeValue = Array.isArray(routeField) ? routeField.join(',') : (routeField || '').toString();
          return queryValue !== routeValue;
        });
        if (anyValueChanged) {
          this.$router.replace({ ...this.$route, query: { ...this.$route.query, ...query } });
        }
      });
    },
    /**
     * @vuese
     * Sprawdza po jakiejś wartości sortować
     */
    getPaginationValue(order) {
      if (order) {
        for (const header of this.headers) {
          if (header.orderBy && header.value === order) {
            return Array.isArray(header.orderBy) ? { orderBy: header.orderBy, order } : { order, orderBy: undefined };
          }
        }
        return { order, orderBy: undefined };
      }
    },
    /**
     * @vuese
     * Ustawienie sortowania
     */
    changeSort(header) {
      if (header.sortable === false) {
        return;
      }
      const order =
        header.order ||
        (header.orderBy && Array.isArray(header.orderBy) && header.orderBy.length > 0 && header.orderBy[0]) ||
        header.orderBy ||
        header.value;
      if (this.pagination.sortBy === order) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = order;
        this.pagination.descending = false;
      }
    },
    /**
     * @vuese
     * Obliczenie wartości do wyświetlenia, jeżeli nagłówek posiada metodę `render` to jest ona wywłana zamiast zwrócenia wartości
     */
    renderValue(item, header) {
      if (header.render) {
        return header.render(item);
      }
      return item[header.value];
    },
    // @vuese
    // Klasy kolumny
    thClasses(header) {
      if (header.sortable === false) {
        return [];
      }
      const classes = ['sortable'];
      if (header.value === this.pagination.sortBy) {
        classes.push(this.pagination.descending ? 'desc' : 'asc');
        classes.push('active');
      }

      return classes;
    }
  }
};
</script>

<style scoped>
.data-table-word-break {
  white-space: normal;
  word-break: break-all;
}

.v-datatable td,
.v-datatable th {
  padding: 0 8px !important;
}
</style>
